<script setup>
const { t } = useI18n();
const setI18nParams = useSetI18nParams();

import { useCommonStore } from '../stores/common';
const store = useCommonStore();
const { setSidebarPhoto } = store;

import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const route = useRoute();

const pageData = gql`
  query ($slug: String!) {
    page(slug: $slug) {
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      seo_url_slug {
        cs
        en
      }
      slug: seo_url_slug {
        cs
        en
      }
      name
      perex
      content
      page_background_color
      image
      cropped_detail_image
      contacts {
        id
        name
        job
        slug: seo_url_slug {
          cs
          en
        }
        image
        group {
          name
        }
        building {
          id
          name
          slug: seo_url_slug {
            cs
            en
          }
        }
      }
      events {
        id
        name
        type
        image: image_list {
          url
        }
        imageCrop: image_list_crop_to
        slug: seo_url_slug {
          cs
          en
        }
        buildings {
          id
          name
        }
        dates {
          id
          start_at {
            timestamp
          }
          end_at {
            timestamp
          }
        }
      }
    }
  }
`;
const { data } = await useAsyncQuery(pageData, { slug: route.params.slug });

const page = computed(() => data.value?.page);
const bgColor = computed(() => page.value?.page_background_color ?? '#00000000');
const content = computed(() => JSON.parse(page.value?.content) || []);

setI18nParams({
  en: {
    slug: page?.value?.seo_url_slug?.en || null,
  },
  cs: {
    slug: page?.value?.seo_url_slug?.cs || null,
  },
});

useHeadData({
  title: page.value?.seo_meta_title || page.value?.name || null,
  description: page.value?.seo_meta_description || null,
  keywords: page.value?.seo_meta_keywords || null,
  image: page.value?.image || page.value?.cropped_detail_image || null,
  url: route.fullPath,
});

if (page.value) {
  if (page.value?.cropped_detail_image) {
    setSidebarPhoto(page.value?.cropped_detail_image);
  }
}

const breadcrumbs = ref([
  {
    title: t('pages.about.index.breadcrumbs-title'),
    route: 'about',
  },
  {
    title: t('pages.collections.title'),
    route: 'about-collections',
  },
  { title: t('pages.collections.detail.breadcrumbs-title') },
]);

onBeforeUnmount(() => {
  setSidebarPhoto(null);
});
</script>
<template>
  <main :style="{ backgroundColor: bgColor, '--background-color': bgColor }" class="main">
    <Breadcrumbs :items="breadcrumbs" />
    <div class="container-content" v-if="page">
      <h1 class="heading">{{ page.name }}</h1>
      <p class="text mt-5" v-html="page.perex"></p>
      <DynamicPageContent v-if="content != null" :content="content" class="mt-4" />
      <ContactList v-if="page.contacts.length" :contacts="page.contacts" />
      <div v-if="page.events.length" class="mt-5">
        <h2 class="heading heading--2 heading--thin">
          {{ t('pages.collection-detail.related.title') }}
        </h2>
        <EventGrid :data="page.events" class="mt-3" />
      </div>
    </div>
  </main>
</template>
